import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, Presentation, ClipboardList, CheckCheck, Inbox, IndianRupee, Lock, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import api from '../lib/api';

const POLL_MS = 30000;

const META = {
  demo: { Icon: Presentation, cls: 'bg-violet-50 text-violet-600', label: 'Demo' },
  credit_note: { Icon: Receipt, cls: 'bg-amber-50 text-amber-700', label: 'Credit note' },
  dpr_missing: { Icon: ClipboardList, cls: 'bg-amber-50 text-amber-600', label: 'DPR' },
  student_overdue: { Icon: IndianRupee, cls: 'bg-rose-50 text-rose-600', label: 'Overdue' },
  student_on_hold: { Icon: Lock, cls: 'bg-amber-50 text-amber-700', label: 'On hold' },
};

function ago(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function TrainerNotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [rows, setRows] = useState([]);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    try { const r = await api.get('/trainer/notifications/unread-count'); setUnread(r.data?.unread || 0); } catch {}
  }, []);

  const loadList = useCallback(async () => {
    try {
      const r = await api.get('/trainer/notifications', { params: { limit: 50 } });
      setRows(r.data?.rows || []);
      setUnread(r.data?.unread_count ?? 0);
    } catch {}
  }, []);

  useEffect(() => {
    refresh();
    timer.current = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer.current);
  }, [refresh]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const openRow = async (n) => {
    if (!n.read && !n.live) { try { await api.post(`/trainer/notifications/${n.id}/read`); } catch {} }
    setOpen(false);
    refresh();
    if (n.source_link) navigate(n.source_link);
  };

  const markAll = async () => { try { await api.post('/trainer/notifications/mark-all-read'); } catch {} loadList(); refresh(); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="relative w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-violet-700 transition-colors" data-testid="trainer-notification-bell">
          <Bell className="w-[18px] h-[18px]" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white" data-testid="trainer-notif-badge">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0 bg-white" data-testid="trainer-notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <p className="text-[13px] font-bold text-slate-800">Notifications</p>
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500" onClick={markAll} data-testid="trainer-notif-mark-all"><CheckCheck className="w-3.5 h-3.5 mr-1" />Mark all read</Button>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {rows.length === 0 ? (
            <div className="py-12 flex flex-col items-center text-slate-400">
              <Inbox className="w-5 h-5 mb-2" /><p className="text-[12px]">You're all caught up.</p>
            </div>
          ) : rows.map((n) => {
            const m = META[n.type] || META.demo;
            return (
              <button key={n.id} onClick={() => openRow(n)} className={`w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-violet-50/60 transition-colors flex gap-3 ${n.read ? 'opacity-60' : ''}`} data-testid={`trainer-notif-${n.id}`}>
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${m.cls}`}><m.Icon className="w-4 h-4" /></span>
                <span className="min-w-0">
                  <span className="block text-[12.5px] font-semibold text-slate-800">{n.title}</span>
                  <span className="block text-[11.5px] text-slate-500 leading-snug">{n.body}</span>
                  <span className="block text-[10.5px] text-slate-400 mt-0.5">{ago(n.created_at)}</span>
                </span>
                {!n.read && <span className="w-2 h-2 rounded-full bg-violet-500 mt-1.5 shrink-0" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
